/*
 * Purpose: Resolve secrets — global vault first, then env fallback for dev/CI
 * Last modified: 2026-06-25 | Agent
 * Completeness: 95
 */
import { useSecretStore } from '@/stores/secretStore';
import { readEnv } from '@/lib/runtimeEnv';

/** Alternate vault key names (checked after primary) */
const VAULT_ALIASES: Record<string, string[]> = {
  LINKEDIN_TOKEN: ['LINKEDIN_ACCESS_TOKEN'],
  LINKEDIN_ACCESS_TOKEN: ['LINKEDIN_TOKEN'],
};

/** Env keys to try after vault (in order) */
const ENV_FALLBACKS: Record<string, string[]> = {
  OPENAI_API_KEY: ['OPENAI_API_KEY', 'VITE_OPENAI_API_KEY'],
  GOOGLE_CLIENT_ID: ['GOOGLE_CLIENT_ID', 'VITE_GOOGLE_CLIENT_ID'],
  GOOGLE_CLIENT_SECRET: ['GOOGLE_CLIENT_SECRET', 'VITE_GOOGLE_CLIENT_SECRET'],
  GOOGLE_ACCESS_TOKEN: ['GOOGLE_ACCESS_TOKEN'],
  LINKEDIN_CLIENT_ID: ['LINKEDIN_CLIENT_ID', 'VITE_LINKEDIN_CLIENT_ID'],
  LINKEDIN_CLIENT_SECRET: ['LINKEDIN_CLIENT_SECRET'],
  LINKEDIN_TOKEN: ['LINKEDIN_TOKEN', 'LINKEDIN_ACCESS_TOKEN'],
  LINKEDIN_ACCESS_TOKEN: ['LINKEDIN_ACCESS_TOKEN', 'LINKEDIN_TOKEN'],
  LINKEDIN_REDIRECT_URI: ['LINKEDIN_REDIRECT_URI', 'VITE_LINKEDIN_REDIRECT_URI'],
  GMAIL_WEBHOOK_SECRET: ['GMAIL_WEBHOOK_SECRET', 'VITE_GMAIL_WEBHOOK_SECRET'],
};

export const COMMON_SECRET_NAMES = [
  'OPENAI_API_KEY',
  'LINKEDIN_CLIENT_ID',
  'LINKEDIN_CLIENT_SECRET',
  'GOOGLE_CLIENT_ID',
  'GOOGLE_ACCESS_TOKEN',
  'LINKEDIN_TOKEN',
] as const;

function resolveFromVault(name: string): string | null {
  const store = useSecretStore.getState();
  const direct = store.getSecret(name);
  if (direct) return direct;

  for (const alt of VAULT_ALIASES[name] ?? []) {
    const v = store.getSecret(alt);
    if (v) return v;
  }
  return null;
}

function resolveFromEnv(name: string): string | null {
  const keys = ENV_FALLBACKS[name] ?? [name, `VITE_${name}`];
  for (const key of keys) {
    const v = readEnv(key);
    if (v) return v;
  }
  return null;
}

/** Vault (when unlocked) wins; env is fallback only. */
export function resolveSecret(name: string): string | null {
  const fromVault = resolveFromVault(name);
  if (fromVault) return fromVault;
  return resolveFromEnv(name);
}

export function hasResolvedSecret(name: string): boolean {
  return Boolean(resolveSecret(name));
}

/** Replace $SECRET_NAME tokens in headers, URLs, and JSON bodies. */
export function substituteSecrets(template: string): string {
  return template.replace(/\$([A-Z][A-Z0-9_]*)/g, (match, key: string) => {
    return resolveSecret(key) ?? match;
  });
}

/** Names available in the HTTP tool picker — vault entries when unlocked, else common list. */
export function listResolvableSecretNames(): string[] {
  const store = useSecretStore.getState();
  if (store.isUnlocked && store.secrets.length > 0) {
    return store.secrets.map((s) => s.name);
  }
  return [...COMMON_SECRET_NAMES];
}
